import { combineReducers, createStore } from 'redux';
import reducer, { loadConfigSuccess, updateNodeProps, updateCanvas, setCurrentScreen } from '../../api/searchExperienceSlice';
import { removeNode } from '../canvas/CanvasBuilder/canvasOperations';

export const LESSON_DRAG_TEXT_ID = 'lesson-drag-text';
export const LESSON_PRODUCT_ID = 'lesson-products';
export const LESSON_STEPS = ['select', 'edit', 'undo', 'tree', 'buildAdd', 'buildTree', 'buildMove', 'buildOrder', 'buildDuplicate', 'buildEdit', 'buildRemove', 'buildRestore', 'screenBefore', 'screenResults', 'screenNoMatches', 'dragAdd', 'dragMove', 'dragOrder'];
export const LESSON_SCREEN_TARGETS = { 12: 'empty', 13: 'results', 14: 'noResults' };

const practiceScreens = (results) => ({
  empty: { canvas: { root: 'lesson-before', nodes: [
    { id: 'lesson-before', type: 'stack', props: { gap: 16 }, slots: { children: ['lesson-history', 'lesson-suggestions'] } },
    { id: 'lesson-history', type: 'history', props: { maxItems: 3 } },
    { id: 'lesson-suggestions', type: 'suggestions', props: { maxItems: 3 } },
  ] } },
  results: { canvas: results },
  noResults: { canvas: { root: 'lesson-no-matches', nodes: [
    { id: 'lesson-no-matches', type: 'noResults', props: {} },
  ] } },
});

// A separate store with no middleware, runtime client or persistence actions.
// The practice configuration never comes from the merchant's working draft.
export const createLessonStore = (checkpoint, t) => {
  const building = checkpoint >= 4;
  const step = building ? Math.min(checkpoint - 4, 8) : checkpoint;
  const store = createStore(combineReducers({ searchExperience: reducer }));
  const products = {
    id: LESSON_PRODUCT_ID,
    type: 'products',
    props: { productsInRow: 4, maxItems: 8, gap: 12, showImage: true, showPrice: true, showTitle: true },
  };
  store.dispatch(loadConfigSuccess({ mode: 'preview', config: {
    search: {
      theme: { colors: { primary: '#26845f' } },
      views: { searchBox: { screens: practiceScreens({
        root: 'lesson-layout',
        nodes: [
          { id: 'lesson-layout', type: 'columns', props: { columns: 2, gap: 24, template: 'minmax(0, 1fr) minmax(0, 3fr)' }, slots: { 'col-1': ['lesson-filters'], 'col-2': [LESSON_PRODUCT_ID] } },
          { id: 'lesson-filters', type: 'facets', props: { limitFacets: 3, collapseKeepOpened: 2 } },
          products,
        ],
      }) } },
    },
  } }));
  // Reconstruct the exercise checkpoint, including an actual undo operation.
  if (!building && step === 2) store.dispatch(updateNodeProps({
    view: 'searchBox', screen: 'results', device: 'desktop', nodeId: products.id,
    props: { ...products.props, productsInRow: 3 },
  }));
  if (building && step > 0) {
    // Recreate a predictable checkpoint; never read the merchant's draft.
    const canvas = store.getState().searchExperience.config.search.views.searchBox.screens.results.canvas;
    const text = { id: 'lesson-text', type: 'text', props: { text: t('experience.lesson.sampleText') } };
    const copy = { ...text, id: 'lesson-text-copy', props: { text: step >= 6 ? t('experience.lesson.copyText') : text.props.text } };
    const column2 = step >= 4 ? [text.id, products.id] : [products.id, ...(step >= 3 ? [text.id] : [])];
    if (step >= 5) column2.splice(column2.indexOf(text.id) + 1, 0, copy.id);
    const nodes = canvas.nodes.map(node => node.id === canvas.root ? { ...node, slots: {
      'col-1': ['lesson-filters', ...(step < 3 ? [text.id] : [])], 'col-2': column2,
    } } : node).concat(text, ...(step >= 5 ? [copy] : []));
    const restoredCanvas = { ...canvas, nodes };
    store.dispatch(loadConfigSuccess({ mode: 'preview', config: {
      ...store.getState().searchExperience.config,
      search: { ...store.getState().searchExperience.config.search, views: { searchBox: { screens: practiceScreens(restoredCanvas) } } },
    } }));
    if (step === 7) store.dispatch(updateCanvas({ view: 'searchBox', screen: 'results', device: 'desktop',
      canvas: { ...restoredCanvas, nodes: removeNode(nodes, canvas.root, copy.id) },
    }));
  }
  if (checkpoint >= 16) {
    const canvas = store.getState().searchExperience.config.search.views.searchBox.screens.results.canvas;
    const slot = checkpoint === 16 ? 'col-1' : 'col-2';
    store.dispatch(updateCanvas({ view: 'searchBox', screen: 'results', device: 'desktop', canvas: {
      ...canvas, nodes: canvas.nodes.map(node => node.id === canvas.root ? {
        ...node, slots: { ...node.slots, [slot]: [...node.slots[slot], LESSON_DRAG_TEXT_ID] },
      } : node).concat({ id: LESSON_DRAG_TEXT_ID, type: 'text', props: { text: t('experience.lesson.dragText') } }),
    } }));
  }
  if (checkpoint === 13) store.dispatch(setCurrentScreen({ screen: 'empty' }));
  return store;
};

export const readLessonProgress = (key, total = LESSON_STEPS.length) => {
  try {
    const value = JSON.parse(window.localStorage.getItem(key));
    // Completed earlier versions continue with the newly appended steps.
    if (value?.status === 'completed' && [4, 12, 15].includes(value.step)) return { status: 'paused', step: value.step };
    return value && ['paused', 'completed', 'dismissed'].includes(value.status)
      && Number.isInteger(value.step) && value.step >= 0
      && (value.status === 'completed' ? value.step === total : value.step < total) ? value : null;
  } catch (_) { return null; }
};

export const writeLessonProgress = (key, progress) => {
  try { window.localStorage.setItem(key, JSON.stringify(progress)); } catch (_) { /* Learning works without browser storage. */ }
};
